import React, { useEffect, useRef, useState } from 'react'
import { cn } from '../../helpers/utils.js'
import type { Nav, NavigationProps } from './types.js'
import { debounce } from 'es-toolkit'

const CpnNavigation = (props: NavigationProps) => {
  const { lists, styles, cns, offset = 0, clickHandler } = props
  const [activeIndex, setActiveIndex] = useState(0)
  const navBox = useRef<HTMLDivElement>(null)

  const scrollIntoView = (ele: HTMLElement) => {
    ele.scrollIntoView({ behavior: 'smooth', inline: 'center' })
  }

  const handleNavItemClick = (nav: Nav, index: number, e: React.SyntheticEvent<EventTarget>) => {
    setActiveIndex(index)
    const ele = e.target as HTMLAnchorElement
    scrollIntoView(ele)
    if (nav.anchor) {
      document.querySelector(`#${nav.anchor}`)?.scrollIntoView({ behavior: 'smooth' })
    }
    e.preventDefault()
    clickHandler && clickHandler(nav, index, e as React.MouseEvent<HTMLAnchorElement>)
  }

  useEffect(() => {
    const scrollHandler = debounce(() => {
      const sectionTops: number[] = []
      lists?.forEach(nav => {
        const section = document.querySelector(`#${nav.anchor}`)
        if (section) {
          sectionTops.push(section.getBoundingClientRect().top)
        }
      })
      const index = sectionTops.findIndex(sectionTop => sectionTop > navHeight + offset)
      setActiveIndex(index === -1 ? sectionTops.length - 1 : index === 0 ? 0 : index - 1)
    }, 100)
    const navHeight = navBox.current?.getBoundingClientRect().height || 0
    window.addEventListener('scroll', scrollHandler)
    return () => {
      window.removeEventListener('scroll', scrollHandler)
    }
  }, [lists, offset])

  return (
    <div
      id="nav"
      className={cn('cpn-nav-container sticky top-0 z-10 w-full bg-[var(--bgColor)]', cns?.container)}
      style={
        {
          '--bgColor': styles?.bgColor || '#C6F1FF',
          '--color': styles?.color || '#333',
          '--activeColor': styles?.activeColor || '#000',
        } as React.CSSProperties
      }
    >
      <div
        className={cn(
          'cpn-nav-box relative flex items-center justify-start gap-[18px] overflow-scroll px-6 text-center [&::-webkit-scrollbar]:hidden',
          'tablet:gap-[80px] tablet:justify-center',
          cns?.box
        )}
        ref={navBox}
      >
        {lists?.map((nav, index) => {
          return (
            <a
              key={nav.label}
              href={`#${nav.anchor}`}
              className={cn(
                'cpn-nav-item relative cursor-pointer whitespace-nowrap py-[14px] text-[16px] font-medium leading-[1.2] text-[var(--color)] transition-all duration-200',
                cns?.item,
                {
                  [`border-[var(--activeColor)] text-[var(--activeColor)] after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-full after:bg-[var(--activeColor)] after:transition-all after:duration-200 after:content-[""] ${cns?.activeItem}`]:
                    activeIndex === index,
                }
              )}
              onClick={e => handleNavItemClick(nav, index, e)}
            >
              {nav.label}
            </a>
          )
        })}
      </div>
    </div>
  )
}

export default CpnNavigation
